export type NavigationSectionKind = 'tab' | 'menu';

export interface NavigationSection {
  visitKey: string;
  label: string;
  href: string;
  kind: NavigationSectionKind;
}

const STORAGE_KEY = 'galidancia_nav_visits';
const PENDING_KEY = 'galidancia_nav_visit_pending';

export const NAVIGATION_SECTIONS: NavigationSection[] = [
  { visitKey: 'tab-clases', label: 'Mis clases', href: '/tabs/clases', kind: 'tab' },
  { visitKey: 'tab-asistencias', label: 'Mis asistencias', href: '/tabs/asistencias', kind: 'tab' },
  { visitKey: 'tab-saldo', label: 'Pagos', href: '/tabs/asientos', kind: 'tab' },
  { visitKey: 'menu-eventos', label: 'Próximos eventos', href: '/tabs/eventos', kind: 'menu' },
  { visitKey: 'menu-mensajes', label: 'Mensajes', href: '/tabs/mensajes', kind: 'menu' },
  { visitKey: 'menu-recuperar', label: 'Recuperar clases', href: '/tabs/recuperar', kind: 'menu' },
  { visitKey: 'menu-probar', label: 'Probar clases', href: '/tabs/probar', kind: 'menu' },
  { visitKey: 'menu-horarios', label: 'Horarios', href: '/tabs/probar', kind: 'menu' },
  { visitKey: 'menu-tienda', label: 'Tienda', href: '/tabs/tienda', kind: 'menu' },
  { visitKey: 'menu-profesores', label: 'Profesores', href: '/tabs/profesores', kind: 'menu' },
  { visitKey: 'menu-coordinadores', label: 'Coordinadores', href: '/tabs/coordinadores', kind: 'menu' },
  { visitKey: 'menu-aulas', label: 'Aulas', href: '/tabs/aulas', kind: 'menu' },
  { visitKey: 'menu-profile', label: 'Mi cuenta', href: '/tabs/profile', kind: 'menu' },
  { visitKey: 'menu-acreditacion', label: 'Acreditación', href: '/tabs/acreditacion', kind: 'menu' },
];

const SECTION_BY_KEY = new Map(NAVIGATION_SECTIONS.map((section) => [section.visitKey, section]));

const ROUTE_VISIT_MAP: Record<string, string> = {
  '/tabs/clases': 'tab-clases',
  '/tabs/asistencias': 'tab-asistencias',
  '/tabs/asientos': 'tab-saldo',
  '/tabs/eventos': 'menu-eventos',
  '/tabs/mensajes': 'menu-mensajes',
  '/tabs/recuperar': 'menu-recuperar',
  '/tabs/probar': 'menu-probar',
  '/tabs/tienda': 'menu-tienda',
  '/tabs/profesores': 'menu-profesores',
  '/tabs/coordinadores': 'menu-coordinadores',
  '/tabs/aulas': 'menu-aulas',
  '/tabs/profile': 'menu-profile',
  '/tabs/acreditacion': 'menu-acreditacion',
};

type VisitCounts = Record<string, number>;

function loadCounts(): VisitCounts {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as VisitCounts;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function saveCounts(counts: VisitCounts): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(counts));
}

export function getNavigationSection(visitKey: string): NavigationSection | undefined {
  return SECTION_BY_KEY.get(visitKey);
}

export function setPendingNavigationVisit(visitKey: string): void {
  sessionStorage.setItem(PENDING_KEY, visitKey);
}

export function consumePendingNavigationVisit(): string | null {
  const pending = sessionStorage.getItem(PENDING_KEY);
  if (pending) sessionStorage.removeItem(PENDING_KEY);
  return pending;
}

export function resolveVisitKeyFromPath(path: string): string | null {
  const normalized = path.replace(/\/+$/, '') || '/';
  if (ROUTE_VISIT_MAP[normalized]) return ROUTE_VISIT_MAP[normalized];

  if (/^\/tabs\/asistencias(\/.*)?$/.test(normalized)) return 'tab-asistencias';
  if (/^\/tabs\/clases(\/.*)?$/.test(normalized)) return 'tab-clases';
  if (/^\/tabs\/mensajes\/chat\//.test(normalized)) return 'menu-mensajes';

  return null;
}

export function registerNavigationVisit(visitKey: string): void {
  if (!visitKey || visitKey === 'menu-inicio') return;
  if (!SECTION_BY_KEY.has(visitKey)) return;

  const counts = loadCounts();
  counts[visitKey] = (counts[visitKey] ?? 0) + 1;
  saveCounts(counts);
}

export function trackNavigationFromRoute(path: string): void {
  const visitKey = consumePendingNavigationVisit() ?? resolveVisitKeyFromPath(path);
  if (visitKey) registerNavigationVisit(visitKey);
}

export interface RankedNavigationSection extends NavigationSection {
  count: number;
}

export function getMostVisitedSections(limit = 12): RankedNavigationSection[] {
  const counts = loadCounts();

  return NAVIGATION_SECTIONS.map((section) => ({
    ...section,
    count: counts[section.visitKey] ?? 0,
  }))
    .filter((section) => section.count > 0)
    .sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return a.label.localeCompare(b.label, 'es', { sensitivity: 'base' });
    })
    .slice(0, limit);
}

export function getVisitCount(visitKey: string): number {
  return loadCounts()[visitKey] ?? 0;
}
